import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, ChevronDown, FileText } from 'lucide-react';
import { SectionHeading } from './Shared';
const bonuses = [{
  id: 1,
  title: '30-Day Volleyball Training Calendar',
  desc: '30 daily practice plans fully written out — balanced across technical, physical and tactical development. Stop guessing what to do each day.',
  pages: 60,
  bgImage: 'https://images.unsplash.com/photo-1544216717-3bbf52512659?q=80&w=2070&auto=format&fit=crop' // Calendar/planning vibe
}, {
  id: 2,
  title: 'Volleyball Drills Encyclopedia',
  desc: 'The drill you need, for any situation, in under 30 seconds. Organized by skill category with full court diagrams, step-by-step instructions, and variations.',
  pages: 60,
  bgImage: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?q=80&w=2070&auto=format&fit=crop' // Volleyball action/spike
}, {
  id: 3,
  title: 'Player Progress Tracker System',
  desc: 'Individual skill assessment sheets, weekly logs and season-end evaluations for every player on your roster. Track real improvement over time.',
  pages: 60,
  bgImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015&auto=format&fit=crop' // Data/tracking/clipboard
}, {
  id: 4,
  title: 'Pre-Season & Game Day Playbook',
  desc: '8-week pre-season plan, tactical systems library, timeout strategy cards and opponent scouting checklist. Win before the whistle blows.',
  pages: 60,
  bgImage: 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?q=80&w=2070&auto=format&fit=crop' // Volleyball team on court
}, {
  id: 5,
  title: 'Volleyball Coach Communication Scripts',
  desc: 'Word-for-word scripts for player feedback, parent emails, halftime speeches and conflict resolution. Handle difficult conversations with confidence.',
  pages: 60,
  bgImage: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?q=80&w=2070&auto=format&fit=crop' // Communication/talking
}];
export const Bonuses = () => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };
  return <section className="py-12 md:py-24 bg-navy-900 text-white relative border-t border-white/10" data-id="element-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-13">
        <SectionHeading className="text-vbgold" data-id="element-14">
          You Also Get These 5 Free Bonuses — Included Today
        </SectionHeading>

        <p className="text-center text-gray-300 mb-10 md:mb-16 max-w-2xl mx-auto text-lg" data-id="element-15">
          We've packed an additional 300 pages of high-value resources to ensure
          you have absolutely everything you need for a successful season.
        </p>

        <div className="space-y-4 md:space-y-6 mb-12 md:mb-16" data-id="element-16">
          {bonuses.map((bonus, idx) => {
          const isExpanded = expandedId === bonus.id;
          return <motion.div key={bonus.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="overflow-hidden border border-white/10 shadow-lg" data-id="element-17">
                <button onClick={() => toggleExpand(bonus.id)} className="w-full text-left relative group focus:outline-none focus:ring-2 focus:ring-vbgold" aria-expanded={isExpanded} data-id="element-18">
                  {/* Banner Image Background */}
                  <div className="absolute inset-0 w-full h-full bg-navy-900" data-id="element-19">
                    <img src={bonus.bgImage} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90" aria-hidden="true" data-id="element-20" />
                    {/* Localized gradient for text readability, leaving the right side clear */}
                    <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/90 to-transparent w-full md:w-2/3" data-id="element-21" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-transparent to-transparent md:hidden" data-id="element-22" />
                  </div>

                  {/* Banner Content */}
                  <div className="relative z-10 p-5 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4 min-h-[120px] md:min-h-[160px]" data-id="element-23">
                    <div className="flex-grow max-w-2xl" data-id="element-24">
                      <div className="flex items-center gap-3 mb-2" data-id="element-25">
                        <div className="bg-vbgold text-navy-900 px-2 py-0.5 text-xs font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 shadow-md" data-id="element-26">
                          <Gift className="w-3 h-3" data-id="element-27" /> Bonus {bonus.id}
                        </div>
                        <div className="bg-navy-900/50 backdrop-blur-md border border-white/20 text-white px-2 py-0.5 text-xs font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 shadow-md" data-id="element-28">
                          <FileText className="w-3 h-3" data-id="element-29" /> {bonus.pages} Pages
                        </div>
                      </div>
                      <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" data-id="element-30">
                        {bonus.title}
                      </h3>
                    </div>

                    <div className="flex items-center gap-4 self-start md:self-center" data-id="element-31">
                      <span className="bg-vbred text-white text-sm md:text-base font-bold px-4 py-2 uppercase tracking-wider shadow-lg" data-id="element-32">
                        Free
                      </span>
                      <div className={`p-2 rounded-full bg-white/10 backdrop-blur-sm transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-33">
                        <ChevronDown className="w-6 h-6 text-white" data-id="element-34" />
                      </div>
                    </div>
                  </div>
                </button>

                <AnimatePresence initial={false} data-id="element-35">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="bg-navy-800 border-t border-white/10" data-id="element-36">
                      <div className="p-5 md:p-8 text-gray-200 text-base md:text-lg leading-relaxed" data-id="element-37">
                        {bonus.desc}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="bg-gradient-to-r from-vbgold/20 via-vbgold/10 to-vbgold/20 border border-vbgold/50 p-6 md:p-8 text-center max-w-3xl mx-auto" data-id="element-38">
          <h3 className="font-display text-2xl md:text-4xl uppercase tracking-tight mb-2" data-id="element-39">
            Total Bonus Value:{' '}
            <span className="text-vbgold line-through opacity-70" data-id="element-40">$182</span>{' '}
            <span className="text-vbred ml-2 block sm:inline mt-1 sm:mt-0" data-id="element-41">
              TODAY FREE
            </span>
          </h3>
          <p className="text-base md:text-lg text-gray-300 font-medium" data-id="element-42">
            Included in your system at no extra cost.
          </p>
        </motion.div>
      </div>
    </section>;
};